import type { AnalyticsRange, StaffAnalyticsAccess } from "@lafayette/schemas";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { deepLinks } from "../../navigation/deep-links";
import { useAreaHref } from "../../navigation/use-offering";
import { Button } from "../../ui/button";
import { Icon } from "../../ui/icon";
import { Notice } from "../../ui/notice";
import { useAnalyticsRuntime } from "../runtime";
import { CustomerPicker } from "./CustomerPicker";
import { RangeControl } from "./RangeControl";

// The header of the cockpit (Paper "Customer analytics"): the eyebrow, the customer name, "Switch customer", the time range and the buttons that
// open the conversation and the support view of the same customer. A refused page shows none of them: they say nothing about a customer that is not yours. "Compare with a segment" and "Export" are not drawn: no segment data and no
// export exist yet, and a control that does nothing is not shown.

function OpenConversation({ customerId }: Readonly<{ customerId: string }>) {
  const { findConversationId } = useAnalyticsRuntime();
  const href = useAreaHref()("concierge", deepLinks.inbox("RECENT"));
  const navigate = useNavigate();
  const [state, setState] = useState<"idle" | "loading" | "none" | "failed">("idle");
  if (findConversationId === undefined || href === null) return null;
  async function go() {
    setState("loading");
    try {
      const id = await (findConversationId as NonNullable<typeof findConversationId>)(customerId);
      if (id === null) setState("none");
      else navigate(deepLinks.conversation(id));
    } catch {
      setState("failed");
    }
  }
  return (
    <>
      <Button type="button" variant="outline" loading={state === "loading"} onClick={go}>
        <Icon name="bubble-chat" size="sm" />
        Open conversation
      </Button>
      {state === "none" || state === "failed" ? (
        <Notice tone={state === "none" ? "info" : "warning"} role="status" className="basis-full">
          {state === "none"
            ? "This customer has no open conversation."
            : "The conversation could not be opened."}
        </Notice>
      ) : null}
    </>
  );
}

export function CockpitHeader({
  access,
  customerId,
  range,
  onRange,
}: Readonly<{
  access: StaffAnalyticsAccess | null;
  customerId: string;
  range: AnalyticsRange;
  onRange: (range: AnalyticsRange) => void;
}>) {
  const [picking, setPicking] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const support = useAreaHref()("customers", deepLinks.customerSupport(customerId));
  return (
    <header className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div className="grid min-w-0 gap-1.5">
          <p className="m-0 text-meta font-semibold tracking-[0.06em] text-muted-foreground uppercase">
            Customer analytics
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1
              id="page-title"
              className="m-0 min-w-0 font-serif text-3xl leading-9 font-medium tracking-[-0.015em] text-balance sm:text-[40px] sm:leading-[44px]"
            >
              {access === null ? "Customer" : access.displayName}
            </h1>
            <Button type="button" variant="outline" size="sm" onClick={() => setPicking(true)}>
              <Icon name="user-switch" size="sm" />
              Switch customer
            </Button>
          </div>
        </div>
        {access === null ? null : (
          <div className="flex flex-wrap items-center gap-2.5">
            <RangeControl value={range} onChange={onRange} />
          </div>
        )}
      </div>
      {access === null ? null : (
        <div className="flex flex-wrap items-center gap-2">
          <OpenConversation customerId={customerId} />
          {support === null ? null : (
            <Button asChild variant="outline">
              <Link to={support}>
                <Icon name="user" size="sm" />
                Support view
              </Link>
            </Button>
          )}
        </div>
      )}
      {picking ? (
        <CustomerPicker
          onClose={() => setPicking(false)}
          onPick={(id) => {
            setPicking(false);
            navigate(deepLinks.customerAnalytics(id, search));
          }}
        />
      ) : null}
    </header>
  );
}
